import {toast, ToastCard} from '@sollapay/ui/components';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

export interface CopyToClipboardOptions {
  fieldName?: string;
  showToast?: boolean;
}

/** `showToast` sets the default; a single `copy()` call can override it. */
export function useCopyToClipboard(
  ns = 'common',
  {showToast = true}: Pick<CopyToClipboardOptions, 'showToast'> = {}
) {
  const {t} = useTranslation(ns);
  const [copied, setCopied] = useState(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(resetTimeoutRef.current), []);

  const copy = useCallback(
    async (
      text: string,
      {fieldName, showToast: showThisToast = showToast}: CopyToClipboardOptions = {}
    ) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
        if (!showThisToast) return;
        toast.custom(id => (
          <ToastCard
            iconName="sollapay:check"
            title={t('copiedToast.title')}
            description={
              fieldName
                ? t('copiedToast.descriptionWithField', {fieldName})
                : t('copiedToast.description')
            }
            onClose={() => toast.dismiss(id)}
            closeLabel={t('copiedToast.close')}
          />
        ));
      } catch {
        // Clipboard API unavailable or permission denied — fail silently
      }
    },
    [t, showToast]
  );

  return {copy, copied};
}
